import { useMemo } from 'react';
import { type GridColumnSorting } from '@adv-frontend/grid';

export type GridColumnSortingDefault<SortKey extends string = string> = {
    key: SortKey;
    order: 'asc' | 'desc';
};

export type UseGridColumnSortingParams<SortKey extends string> = {
    /** Ключ сортировки из URL/state; `undefined` — в гриде без активной сортировки (порядок строк задаёт бэк). */
    sortKey: string | undefined;
    /** Направление для `sortKey` (при пустом `sortKey` на отображение влияет только `defaultSort`). */
    sortOrder: 'asc' | 'desc';
    /** Записать новую сортировку (обновить query/URL/state). */
    onSortChange: (sortKey: SortKey, sortOrder: 'asc' | 'desc') => void;
    /** Проверка: id колонки грида совпадает с допустимым ключом API. */
    isValidSortKey: (key: string) => key is SortKey;
    /** Сортировка при сбросе в гриде, невалидном id колонки и при отсутствии явного `sortKey`. */
    defaultSort: GridColumnSortingDefault<SortKey>;
};

/**
 * Хук для управления состоянием сортировки.
 *
 * @kind helper
 * @typeParam SortKey
 * @returns {GridColumnSorting}
 */
export function useGridColumnSorting<SortKey extends string>({
    sortKey,
    sortOrder,
    onSortChange,
    isValidSortKey,
    defaultSort,
}: UseGridColumnSortingParams<SortKey>): GridColumnSorting {
    const columnSortingState = useMemo(() => {
        const key = sortKey?.trim();

        if (key) {
            return [{ id: key, desc: sortOrder !== 'asc' }];
        }

        return [{ id: defaultSort.key, desc: defaultSort.order !== 'asc' }];
    }, [sortKey, sortOrder, defaultSort]);

    return useMemo<GridColumnSorting>(() => {
        return {
            columnSorting: columnSortingState,
            setColumnSorting: (nextColumnSorting) => {
                const columnSorting =
                    typeof nextColumnSorting === 'function' ? nextColumnSorting(columnSortingState) : nextColumnSorting;

                const columnId = columnSorting[0]?.id;

                if (columnId !== undefined && isValidSortKey(columnId)) {
                    onSortChange(columnId, columnSorting[0]?.desc ? 'desc' : 'asc');
                } else {
                    onSortChange(defaultSort.key, defaultSort.order);
                }
            },
        };
    }, [columnSortingState, onSortChange, isValidSortKey, defaultSort]);
}
